import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { MicButton } from "./MicButton";
import { t } from "../../i18n/strings";
import { getVoiceCapabilities } from "../../voice/capabilities";
import { useDictation } from "../../voice/useDictation";
import type { Locale } from "../../templates/types";
import type { InputMode } from "../../engine/types";

export function Composer({
  onSend,
  disabled,
  placeholder,
  locale = "en",
}: {
  onSend: (text: string, inputMode?: InputMode) => void;
  disabled: boolean;
  placeholder?: string;
  locale?: Locale;
}) {
  const [value, setValue] = useState("");
  // Tracks whether the text currently in the box came from a dictation final
  // result rather than typing, so Send can tag the resulting turn's
  // provenance with `inputMode: 'voice'` (§8 — voice values are ASR output,
  // not literally what was said). Any manual keystroke reverts this to
  // "typed": once a person edits the text with a keyboard it is no longer
  // pure ASR output.
  const [inputMode, setInputMode] = useState<InputMode>("typed");
  const resolvedPlaceholder = placeholder ?? t(locale, "chat.placeholder.default");

  // Feature-detected once per render via capabilities.ts, not cached — cheap
  // and keeps this the single source of truth for "does voice work here."
  const { canListen } = getVoiceCapabilities();
  const dictation = useDictation();

  useEffect(() => {
    // If the composer becomes disabled (paused, awaiting model, complete)
    // while dictation is live, stop listening rather than leaving the mic
    // hot with nowhere for its output to go.
    if (disabled && dictation.listening) {
      dictation.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (disabled || !value.trim()) return;
    if (dictation.listening) dictation.stop();
    // Typed path calls onSend with a single argument, matching the prior
    // (pre-voice) behavior exactly — the inputMode parameter defaults to
    // "typed" wherever it's consumed (see useInterviewRunner.sendMessage).
    if (inputMode === "voice") {
      onSend(value, "voice");
    } else {
      onSend(value);
    }
    setValue("");
    setInputMode("typed");
  }

  function handleMicToggle() {
    if (dictation.listening) {
      dictation.stop();
      return;
    }
    dictation.start(locale, {
      onFinalResult: (text) => {
        // §16: the final transcript populates the input for review and
        // NEVER auto-sends — it only lands in the same box the participant
        // would have typed into, so the existing Send / Enter-to-send flow
        // is what actually submits it.
        setValue((prev) => (prev.trim() ? `${prev.trim()} ${text}`.trim() : text));
        setInputMode("voice");
      },
    });
  }

  return (
    <div className="border-t border-border bg-paper p-3">
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <label htmlFor="composer-input" className="sr-only">
          {t(locale, "chat.composer.srLabel")}
        </label>
        <div className="flex-1">
          <textarea
            id="composer-input"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setInputMode("typed");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            disabled={disabled}
            placeholder={resolvedPlaceholder}
            rows={1}
            className="min-h-10 w-full resize-none rounded-lg border border-border bg-paper px-3 py-2 text-sm text-ink placeholder:text-ink-soft focus:outline-none disabled:opacity-50"
          />
          {dictation.listening && dictation.interimTranscript && (
            // Interim (non-final) ASR output, rendered clearly distinct from
            // confirmed/typed text — never merged into the value as if it
            // were already confirmed (§16).
            <p className="mt-1 truncate text-sm italic text-ink-soft" aria-live="polite">
              {dictation.interimTranscript}
            </p>
          )}
        </div>
        <MicButton
          canListen={canListen}
          listening={dictation.listening}
          onToggle={handleMicToggle}
          locale={locale}
          disabled={disabled}
        />
        <Button type="submit" disabled={disabled || !value.trim()}>
          {t(locale, "chat.composer.send")}
        </Button>
      </form>
      {!canListen && <p className="mt-2 text-xs text-ink-soft">{t(locale, "voice.unsupported")}</p>}
    </div>
  );
}
